import { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cn } from './utils';

/**
 * iOS scroll-edge-blur band.
 *
 * Renders a layered blur strip pinned to the top of a scroll container so
 * content slides *under* it with a soft feathered edge — matches the
 * appearance Apple uses for a navigation bar with `scrollEdgeAppearance`.
 *
 * Layers (bottom → top):
 *   1. Baseline blur — light `blur(5px)` across the strip, masked by a
 *      vertical gradient so it eases out instead of terminating in a hard
 *      line. Always-on so even un-scrolled content gets a subtle tint.
 *   2. Heavy blur layer — `blur(30px) saturate(180%)` masked by a vertical
 *      multi-stop gradient that eases out over the last ~half of the band,
 *      giving a soft feather rather than a hard cut-off line.
 *   3. `children` — your own title row / pills / breadcrumbs sit on top,
 *      typically as an absolutely-positioned element anchored to top:0.
 *
 * Both blur layers are masked: the `backdrop-filter` clip box would
 * otherwise leave a visible hardline at the band's bottom edge regardless
 * of how the heavy layer is feathered.
 *
 * The band is `pointer-events: none` so empty fade space passes touches
 * through to scrolling content; interactive children inside `children`
 * should set their own `pointer-events: auto` (or use a button element).
 */
export type ScrollEdgeBlurPosition = 'sticky' | 'absolute' | 'static';

export interface ScrollEdgeBlurProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Total band height. Number → pixels, string → raw CSS (e.g.
   * `'var(--qs-floating-header-clearance, 0px)'`). Default `64`.
   */
  height?: number | string;
  /**
   * Positioning strategy:
   * - `sticky` (default) — sticky to top of nearest scroll ancestor; content
   *   scrolls *under* the band. Best inside a scroll container.
   * - `absolute` — overlay positioned relative to nearest positioned ancestor.
   *   Use when the band must float over a sibling scroll container.
   * - `static` — plain flex/block element, no special positioning. Best as a
   *   sibling above a scroll container — touch events on it never compete
   *   with the scroll container.
   */
  position?: ScrollEdgeBlurPosition;
  /** @deprecated use `position="absolute"` instead. */
  absolute?: boolean;
}

export function ScrollEdgeBlur({
  height = 64,
  position,
  absolute,
  className,
  children,
  style,
  ...rest
}: ScrollEdgeBlurProps) {
  const resolvedPosition: ScrollEdgeBlurPosition =
    position ?? (absolute ? 'absolute' : 'sticky');
  const wrapperStyle: CSSProperties = {
    height: typeof height === 'number' ? `${height}px` : height,
    background: 'transparent',
    ...style,
  };
  const positionClass =
    resolvedPosition === 'absolute'
      ? 'absolute top-0 left-0 right-0'
      : resolvedPosition === 'static'
        ? 'relative'
        : 'sticky top-0';

  return (
    <div
      data-slot="scroll-edge-blur"
      className={cn(positionClass, 'z-40 flex-shrink-0', className)}
      style={wrapperStyle}
      {...rest}
    >
      {/* Baseline blur with eased mask — a `backdrop-filter` clips to its own
          box, so an unmasked baseline would leave a hard line at the band's
          bottom edge. The mask feathers it out over the lower third. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          maskImage:
            'linear-gradient(180deg, #000 0%, #000 60%, rgba(0,0,0,0.6) 78%, rgba(0,0,0,0.25) 90%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'linear-gradient(180deg, #000 0%, #000 60%, rgba(0,0,0,0.6) 78%, rgba(0,0,0,0.25) 90%, rgba(0,0,0,0) 100%)',
        }}
      />
      {/* Heavy blur layer with eased mask — multi-stop gradient gives a soft
          feathered edge instead of a hard linear cut-off. `pointer-events:
          none` ONLY on this visual layer so touches pass through to the
          scroll container and inertial scroll engages even when starting
          from the header area (iOS Mail / Reminders behaviour). */}
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          maskImage:
            'linear-gradient(180deg, #000 0%, #000 50%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.25) 90%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'linear-gradient(180deg, #000 0%, #000 50%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.25) 90%, rgba(0,0,0,0) 100%)',
        }}
      />
      {children}
    </div>
  );
}
